import { useCallback, useState, useEffect, useRef } from 'react';
import { KanbanCard, KanbanProps, KanbanRegularColumn } from './HBKanban.types';
import { useKanbanInfiniteScroll } from './useKanbanInfiniteScroll';
import { DragStartEvent, DragOverEvent, DragEndEvent } from '@dnd-kit/core';
import {
    getCardById,
    findCardColumn,
    moveCards,
    revertCards,
    CardOriginalPosition,
    calculateColumnCounts,
    removeCard as removeCardUtil,
    restoreCard
} from './kanbanUtils';
import React from 'react';

/**
 * Props for the useKanban hook
 * Picks only the relevant props from KanbanProps
 */
type UseKanbanProps<TItemData = any, TColumnData = any> = Pick<
    KanbanProps<TItemData, TColumnData>,
    | 'items'
    | 'columns'
    | 'dragDropHandlers'
    | 'shouldRejectDrop'
    | 'infiniteScroll'
    | 'isSelectable'
>;

type ItemsMap<TItemData = any> = Record<string, KanbanCard<TItemData>[]>;

/**
 * Custom hook to access the KanbanContext value.
 * Throws if used outside a KanbanProvider.
 */
export const useKanban = <TItemData = any,>({
    items,
    columns,
    dragDropHandlers,
    shouldRejectDrop,
    infiniteScroll,
    isSelectable = false
}: UseKanbanProps<TItemData, any>) => {
    const [internalItems, setInternalItems] = useState<ItemsMap<TItemData>>(
        (items || {}) as ItemsMap<TItemData>
    );

    const regularColumns = (columns ?? []).filter(
        col => col.type !== 'divider'
    ) as KanbanRegularColumn<any>[];

    const [columnCounts, setColumnCounts] = useState<Record<string, number>>(
        calculateColumnCounts(regularColumns, internalItems)
    );

    const previousColumnCountsRef = useRef<Record<string, number> | null>(null);
    const internalItemsSnapshotRef = useRef<ItemsMap<TItemData>>(internalItems);

    // Callback for infinite scroll to append items directly to internal state
    const onAppendItems = useCallback(
        (columnId: string, newItems: KanbanCard<TItemData>[]) => {
            setInternalItems(prev => ({
                ...prev,
                [columnId]: [...(prev[columnId] || []), ...newItems]
            }));
        },
        []
    );

    const infiniteScrollState = useKanbanInfiniteScroll({
        columns: regularColumns || [],
        infiniteScrollProps: infiniteScroll,
        onAppendItems
    });

    useEffect(() => {
        internalItemsSnapshotRef.current = internalItems;
    }, [internalItems]);
    const [isInSelectableState, setIsInSelectableState] = useState(false);
    const [selectedCards, setSelectedCards] = useState<KanbanCard<TItemData>[]>(
        []
    );

    const [activeIds, setActiveIds] = useState<string[]>([]);
    const [overId, setOverId] = useState<string | null>(null);
    const [draggingOverNonDroppable, setDraggingOverNonDroppable] = useState<
        string | null
    >(null);
    const [lastTargetColumnId, setLastTargetColumnId] = useState<string | null>(
        null
    );

    const originalSourceColumnsRef = useRef<Map<string, string>>(new Map());
    const originalIndicesRef = useRef<Map<string, number>>(new Map());
    const stateAtDragStartRef = useRef<Record<
        string,
        KanbanCard<TItemData>[]
    > | null>(null);
    const isDraggingRef = useRef(false);
    const columnContentRefsRef = useRef<
        Map<string, React.RefObject<HTMLDivElement>>
    >(new Map());
    const columnRefsRef = useRef<Map<string, React.RefObject<HTMLDivElement>>>(
        new Map()
    );
    const [columnRefsVersion, setColumnRefsVersion] = useState(0);

    useEffect(() => {
        if (isDraggingRef.current) {
            return;
        }
        const filteredColumns = (columns ?? []).filter(
            col => col.type !== 'divider'
        ) as KanbanRegularColumn<any>[];
        const newCounts = calculateColumnCounts(
            filteredColumns,
            internalItemsSnapshotRef.current
        );

        setColumnCounts(prevCounts => {
            const hasChanged =
                Object.keys(newCounts).some(
                    columnId => prevCounts[columnId] !== newCounts[columnId]
                ) ||
                Object.keys(prevCounts).some(
                    columnId => !(columnId in newCounts)
                );
            return hasChanged ? newCounts : prevCounts;
        });
    }, [columns]);

    const handleDragStart = useCallback(
        (card: DragStartEvent) => {
            const cardIds =
                isSelectable && selectedCards.length > 0
                    ? selectedCards.map(selectedCard => selectedCard.id)
                    : [card.active.id as string];

            isDraggingRef.current = true;

            setActiveIds(cardIds);
            setDraggingOverNonDroppable(null);

            stateAtDragStartRef.current = Object.keys(internalItems).reduce(
                (acc, columnId) => {
                    acc[columnId] = [...internalItems[columnId]];
                    return acc;
                },
                {} as Record<string, KanbanCard<TItemData>[]>
            );

            previousColumnCountsRef.current = { ...columnCounts };

            const sourceColumns = new Map<string, string>();
            cardIds.forEach(cardId => {
                const sourceColumnId = findCardColumn(internalItems, cardId);
                if (sourceColumnId) {
                    sourceColumns.set(cardId, sourceColumnId);
                }
            });
            originalSourceColumnsRef.current = sourceColumns;

            const originalIndices = new Map<string, number>();
            Object.values(internalItems).forEach(columnCards => {
                columnCards.forEach((cardItem, index) => {
                    originalIndices.set(cardItem.id, index);
                });
            });
            originalIndicesRef.current = originalIndices;

            if (dragDropHandlers?.onDragStart) {
                const draggedCards = cardIds
                    .map(cardId => getCardById(internalItems, cardId))
                    .filter(
                        (draggedCard): draggedCard is KanbanCard<TItemData> =>
                            Boolean(draggedCard)
                    );
                if (draggedCards.length > 0) {
                    dragDropHandlers.onDragStart(draggedCards);
                }
            }
        },
        [
            dragDropHandlers,
            selectedCards,
            internalItems,
            isSelectable,
            columnCounts
        ]
    );

    const handleDragOver = useCallback(
        (card: DragOverEvent) => {
            const { active, over } = card;
            const aId = active.id as string;

            if (!over) {
                setOverId(null);
                setLastTargetColumnId(null);
                if (dragDropHandlers?.onDragOver) {
                    const activeCard = getCardById(internalItems, aId);
                    if (activeCard) {
                        dragDropHandlers.onDragOver({ cards: [activeCard] });
                    }
                }
                return;
            }

            setOverId((over?.id as string) || null);

            const overData = over.data.current;
            const isOverACard = overData?.type === 'kanban-card';

            const oId = over.id as string;

            if (aId === oId && isOverACard) {
                return;
            }

            let targetColumnId: string | null = null;

            if (isOverACard) {
                targetColumnId = findCardColumn(internalItems, oId);
            } else {
                targetColumnId = oId;
            }

            if (!targetColumnId) {
                setLastTargetColumnId(null);
                setDraggingOverNonDroppable(null);
                if (dragDropHandlers?.onDragOver) {
                    const activeCard = getCardById(internalItems, aId);
                    if (activeCard) {
                        dragDropHandlers.onDragOver({ cards: [activeCard] });
                    }
                }
                return;
            }

            const targetColumn = columns?.find(
                col => col.id === targetColumnId
            );

            let isNonDroppable = false;
            if (targetColumn && targetColumn.type !== 'divider') {
                const activeCard = getCardById(internalItems, aId);
                if (activeCard && shouldRejectDrop) {
                    const sourceColumnId = findCardColumn(internalItems, aId);
                    const sourceColumn = sourceColumnId
                        ? columns?.find(col => col.id === sourceColumnId)
                        : null;

                    // If sourceColumn is null, we can't validate, so allow the drop (will be caught in handleDragEnd)
                    if (sourceColumn) {
                        const shouldReject = shouldRejectDrop({
                            card: activeCard,
                            sourceColumn,
                            targetColumn: targetColumn as any
                        });
                        isNonDroppable = shouldReject;
                    }
                }
            }

            if (targetColumnId === lastTargetColumnId) {
                setDraggingOverNonDroppable(
                    isNonDroppable ? targetColumnId : null
                );
                if (dragDropHandlers?.onDragOver) {
                    const activeCard = getCardById(internalItems, aId);
                    const targetColumnForOver = columns?.find(
                        col =>
                            col.id === targetColumnId && col.type !== 'divider'
                    ) as any;
                    if (activeCard) {
                        dragDropHandlers.onDragOver({
                            cards: [activeCard],
                            targetColumn: targetColumnForOver
                        });
                    }
                }
                return;
            }

            setLastTargetColumnId(targetColumnId);
            setDraggingOverNonDroppable(isNonDroppable ? targetColumnId : null);

            if (dragDropHandlers?.onDragOver) {
                const activeCard = getCardById(internalItems, aId);
                const targetColumnForOver = columns?.find(
                    col => col.id === targetColumnId && col.type !== 'divider'
                ) as any;
                if (activeCard) {
                    dragDropHandlers.onDragOver({
                        cards: [activeCard],
                        targetColumn: targetColumnForOver
                    });
                }
            }
        },
        [
            dragDropHandlers,
            lastTargetColumnId,
            columns,
            internalItems,
            shouldRejectDrop
        ]
    );

    const handleDragEnd = useCallback(
        async (card: DragEndEvent) => {
            const { active, over } = card;

            const aId = active.id as string;

            isDraggingRef.current = false;

            const originalSourceColumns = originalSourceColumnsRef.current;
            const cardsToMove: KanbanCard<TItemData>[] =
                isSelectable && selectedCards.length > 0
                    ? selectedCards
                          .map(selectedCard =>
                              getCardById(internalItems, selectedCard.id)
                          )
                          .filter(
                              (movedCard): movedCard is KanbanCard<TItemData> =>
                                  Boolean(movedCard)
                          )
                    : ([getCardById(internalItems, aId)].filter(
                          Boolean
                      ) as KanbanCard<TItemData>[]);

            if (cardsToMove.length === 0) {
                setActiveIds([]);
                setOverId(null);
                setLastTargetColumnId(null);
                originalSourceColumnsRef.current = new Map();
                // Revert column counts if they were stored
                if (previousColumnCountsRef.current) {
                    setColumnCounts(previousColumnCountsRef.current);
                    previousColumnCountsRef.current = null;
                }
                const revertState = stateAtDragStartRef.current;
                if (revertState) {
                    setInternalItems(revertState);
                }
                stateAtDragStartRef.current = null;
                return;
            }

            let targetColumnId: string | null = null;
            let isValidDrop = false;
            let isSameColumnDrop = false;

            if (!over) {
                isValidDrop = false;
            } else {
                const oId = over.id as string;
                const overData = over.data.current;
                const isOverACard = overData?.type === 'kanban-card';

                if (isOverACard) {
                    targetColumnId = findCardColumn(internalItems, oId);
                } else {
                    targetColumnId = oId;
                }

                if (!targetColumnId) {
                    isValidDrop = false;
                } else {
                    // Find the target column
                    const targetColumn = columns.find(
                        col => col.id === targetColumnId
                    );

                    if (targetColumn && targetColumn.type !== 'divider') {
                        // Block reordering within the same column
                        const allCardsFromTargetColumn = cardsToMove.every(
                            movedCard => {
                                const sourceColumnId =
                                    originalSourceColumns.get(movedCard.id);
                                return sourceColumnId === targetColumnId;
                            }
                        );

                        if (allCardsFromTargetColumn) {
                            isSameColumnDrop = true;
                            isValidDrop = false;
                        } else {
                            if (shouldRejectDrop) {
                                const noCardsRejected = cardsToMove.every(
                                    movedCard => {
                                        const sourceColumnId =
                                            originalSourceColumns.get(
                                                movedCard.id
                                            );
                                        const sourceColumn = sourceColumnId
                                            ? columns.find(
                                                  col =>
                                                      col.id === sourceColumnId
                                              )
                                            : null;

                                        if (!sourceColumn) {
                                            return false;
                                        }

                                        const shouldReject = shouldRejectDrop({
                                            card: movedCard,
                                            sourceColumn,
                                            targetColumn: targetColumn as any
                                        });

                                        if (
                                            shouldReject &&
                                            dragDropHandlers?.onDropReject
                                        ) {
                                            dragDropHandlers.onDropReject({
                                                card: movedCard,
                                                sourceColumn,
                                                targetColumn:
                                                    targetColumn as any
                                            });
                                        }

                                        return !shouldReject;
                                    }
                                );
                                isValidDrop = noCardsRejected;
                            } else {
                                isValidDrop = true;
                            }
                        }
                    } else {
                        isValidDrop = false;
                    }
                }
            }

            setActiveIds([]);
            setOverId(null);
            setDraggingOverNonDroppable(null);
            setLastTargetColumnId(null);
            originalSourceColumnsRef.current = new Map();

            if (!isValidDrop) {
                if (isSameColumnDrop) {
                    // Cards are already in correct position, no need to revert
                    stateAtDragStartRef.current = null;
                    previousColumnCountsRef.current = null;
                    return;
                } else {
                    if (previousColumnCountsRef.current) {
                        setColumnCounts(previousColumnCountsRef.current);
                        previousColumnCountsRef.current = null;
                    }
                    const revertState = stateAtDragStartRef.current;
                    if (revertState) {
                        setInternalItems(revertState);
                    }
                    stateAtDragStartRef.current = null;
                    return;
                }
            }

            stateAtDragStartRef.current = null;

            setColumnCounts(currentCounts => {
                const newCounts = { ...currentCounts };
                const cardsBySourceColumn = new Map<string, number>();

                cardsToMove.forEach(cardToMove => {
                    const sourceColumnId = originalSourceColumns.get(
                        cardToMove.id
                    );
                    if (sourceColumnId) {
                        cardsBySourceColumn.set(
                            sourceColumnId,
                            (cardsBySourceColumn.get(sourceColumnId) || 0) + 1
                        );
                    }
                });

                cardsBySourceColumn.forEach((count, sourceColumnId) => {
                    newCounts[sourceColumnId] = Math.max(
                        0,
                        (newCounts[sourceColumnId] || 0) - count
                    );
                });

                newCounts[targetColumnId!] =
                    (newCounts[targetColumnId!] || 0) + cardsToMove.length;

                return newCounts;
            });

            setInternalItems(currentInternalItems => {
                const moves = cardsToMove.map(cardToMove => ({
                    cardId: cardToMove.id,
                    sourceColumnId:
                        originalSourceColumns.get(cardToMove.id) || '',
                    targetColumnId: targetColumnId!
                }));

                const newItems = moveCards(currentInternalItems, moves);

                Object.keys(currentInternalItems).forEach(columnId => {
                    if (!newItems[columnId]) {
                        newItems[columnId] = [];
                    }
                });

                // Scroll target column to top after drop
                requestAnimationFrame(() => {
                    const targetColumnRef = columnContentRefsRef.current.get(
                        targetColumnId!
                    );
                    if (targetColumnRef?.current) {
                        targetColumnRef.current.scrollTop = 0;
                    }
                });

                return newItems;
            });

            if (dragDropHandlers?.onDragEnd && isValidDrop) {
                // TODO: This isn't really ready for dragging multiple cards from different columns
                const sourceColumnId = originalSourceColumns.get(
                    cardsToMove[0].id
                );
                const sourceColumn = sourceColumnId
                    ? columns.find(col => col.id === sourceColumnId)
                    : null;
                const targetColumn = columns.find(
                    col => col.id === targetColumnId
                );

                if (
                    sourceColumn &&
                    targetColumn &&
                    targetColumn.type !== 'divider'
                ) {
                    const revertDrag = () => {
                        if (previousColumnCountsRef.current) {
                            setColumnCounts(previousColumnCountsRef.current);
                            previousColumnCountsRef.current = null;
                        }

                        const originalPositions = new Map<
                            string,
                            CardOriginalPosition
                        >();
                        cardsToMove.forEach(cardToRevert => {
                            const sourceColId = originalSourceColumns.get(
                                cardToRevert.id
                            );
                            const originalIndex =
                                originalIndicesRef.current.get(cardToRevert.id);
                            if (sourceColId && originalIndex !== undefined) {
                                originalPositions.set(cardToRevert.id, {
                                    sourceColumnId: sourceColId,
                                    originalIndex
                                });
                            }
                        });

                        setInternalItems(currentInternalItems => {
                            const revertedItems = revertCards(
                                currentInternalItems,
                                cardsToMove.map(
                                    cardToRevert => cardToRevert.id
                                ),
                                originalPositions,
                                originalIndicesRef.current
                            );

                            Object.keys(currentInternalItems).forEach(
                                columnId => {
                                    if (!revertedItems[columnId]) {
                                        revertedItems[columnId] = [];
                                    }
                                }
                            );

                            return revertedItems;
                        });
                    };

                    await dragDropHandlers.onDragEnd({
                        cards: cardsToMove,
                        sourceColumn,
                        targetColumn: targetColumn as any,
                        revertDrag
                    });
                }
            }
        },
        [
            selectedCards,
            internalItems,
            columns,
            dragDropHandlers,
            isSelectable,
            shouldRejectDrop
        ]
    );

    const getActiveCard = useCallback(() => {
        return activeIds.length > 0
            ? getCardById(internalItems, activeIds[0])
            : null;
    }, [activeIds, internalItems]);

    const getCardByIdWrapper = useCallback(
        (cardId: string) => getCardById(internalItems, cardId),
        [internalItems]
    );

    const canDropInColumn = useCallback(
        (cardId: string, columnId: string) => {
            const card = getCardById(internalItems, cardId);
            const column = columns.find(col => col.id === columnId);

            if (!card || !column || column.type === 'divider') {
                return true; // Allow by default
            }

            if (shouldRejectDrop) {
                const sourceColumnId = findCardColumn(internalItems, cardId);
                const sourceColumn = sourceColumnId
                    ? columns.find(col => col.id === sourceColumnId)
                    : null;

                if (!sourceColumn) {
                    return true; // Allow by default if source column not found
                }

                const shouldReject = shouldRejectDrop({
                    card,
                    sourceColumn,
                    targetColumn: column as any
                });

                return !shouldReject;
            }

            return true;
        },
        [columns, internalItems, shouldRejectDrop]
    );

    const getSourceColumnForCard = useCallback((cardId: string) => {
        return originalSourceColumnsRef.current.get(cardId) || null;
    }, []);

    const registerColumnContentRef = useCallback(
        (columnId: string, ref: React.RefObject<HTMLDivElement>) => {
            columnContentRefsRef.current.set(columnId, ref);
        },
        []
    );

    const registerColumnRef = useCallback(
        (columnId: string, ref: React.RefObject<HTMLDivElement>) => {
            columnRefsRef.current.set(columnId, ref);
            setColumnRefsVersion(v => v + 1);
        },
        []
    );

    useEffect(() => {
        if (isSelectable) {
            setIsInSelectableState(selectedCards.length > 0);
        } else {
            setIsInSelectableState(false);
        }
    }, [selectedCards, isSelectable]);

    const isCardSelected = useCallback(
        (card: KanbanCard<TItemData>) => {
            if (!isSelectable) {
                return false;
            }
            return selectedCards.some(c => c.id === card.id);
        },
        [selectedCards, isSelectable]
    );

    const onToggleSelect = useCallback(
        (card: KanbanCard<TItemData>) => {
            if (!isSelectable) {
                return;
            }
            setSelectedCards(prev => {
                const isCurrentlySelected = prev.some(c => c.id === card.id);
                const newState = isCurrentlySelected
                    ? prev.filter(c => c.id !== card.id)
                    : [...prev, card];

                return newState;
            });
        },
        [isSelectable]
    );

    // Function to remove a card from internal state
    // Returns a revertDelete function that can be called if deletion fails
    // Each revert function captures its own card info to handle multiple concurrent deletions
    const removeCard = useCallback(
        (cardId: string): (() => void) | undefined => {
            const card = getCardById(internalItems, cardId);
            if (!card) {
                return undefined;
            }

            const columnId = findCardColumn(internalItems, cardId);
            if (!columnId) {
                return undefined;
            }

            // Find the original index
            const columnCards = internalItems[columnId] || [];
            const originalIndex = columnCards.findIndex(c => c.id === cardId);

            // Capture card info in closure for this specific deletion
            // This ensures each revert function has its own card info,
            // allowing multiple concurrent deletions to be reverted independently
            const deletedCard = card;
            const deletedColumnId = columnId;
            const deletedIndex = originalIndex;

            // Remove the card optimistically
            setInternalItems(prevItems => removeCardUtil(prevItems, cardId));

            // Return revert function that captures this specific card's info
            return () => {
                setInternalItems(prevItems =>
                    restoreCard(
                        prevItems,
                        deletedCard,
                        deletedColumnId,
                        deletedIndex
                    )
                );
            };
        },
        [internalItems]
    );

    return {
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        getCardById: getCardByIdWrapper,
        getActiveCard,
        canDropInColumn,
        getSourceColumnForCard,
        activeIds,
        overId,
        draggingOverNonDroppable,
        internalItems,
        columnCounts,
        selectedCards: isSelectable ? selectedCards : [],
        isInSelectableState: isSelectable ? isInSelectableState : false,
        isCardSelected: isSelectable ? isCardSelected : () => false,
        onToggleSelect: isSelectable ? onToggleSelect : undefined,
        onLoadMore: infiniteScroll
            ? infiniteScrollState.handleLoadMore
            : undefined,
        isLoadingColumn: infiniteScroll
            ? infiniteScrollState.isLoadingColumn
            : undefined,
        removeCard,
        columnContentRefs: columnContentRefsRef.current,
        registerColumnContentRef,
        columnRefs: columnRefsRef.current,
        registerColumnRef,
        columnRefsVersion
    };
};
